import { Blueprint } from '../blueprint.class';
import { DesignContext } from '../types';
import { isExcludedCfn } from './ai-tools/is-excluded.cfn';

const escapeBackticks = (text: string): string => text.replace(/`/g, '\\`');

const formatLastModified = (blueprint: Blueprint): string => {
	const raw = blueprint.submissionDate;
	if (!raw) return 'unknown';
	return raw.replace(/\.\d{3}Z$/, 'Z');
};

export const getDesignContextCfn = (blueprint: Blueprint): DesignContext => {
	const idMap: Record<string, string> = {};
	const lines: Array<string> = ['## Current Design', ''];

	const kitchen = blueprint.kitchens.children.length > 0 ? blueprint.kitchens.getActiveVersion() : null;
	if (!kitchen || kitchen.design.islands.children.length === 0) {
		lines.push('_No islands placed yet._');
		return { prompt: lines.join('\n'), idMap };
	}

	const islands = kitchen.design.islands.children;
	let totalAppliances = 0;

	islands.forEach((island, iZeroBased) => {
		const i = iZeroBased + 1;
		const islandToken = `island-${i}`;
		idMap[islandToken] = island.id;

		lines.push(`**Island ${i}** — ${island.type}, ${island.size.width}" wide (\`${islandToken}\`)`);

		const appliances = island.panels.children
			.flatMap((panel) => panel.panelItemGroups.children)
			.flatMap((group) => group.items.children)
			.filter((panelItem) => !isExcludedCfn(panelItem));

		appliances.forEach((panelItem, jZeroBased) => {
			const j = jZeroBased + 1;
			const appToken = `${islandToken}.app-${j}`;
			idMap[appToken] = panelItem.id;

			const { component } = panelItem.item;
			const { sku, displayText } = panelItem.item;
			const aiContext = component.aiContext;
			const fallback = `${component.category.description} (${sku})`;
			const detail = aiContext && aiContext.trim().length > 0 ? aiContext : fallback;

			lines.push(`- \`${appToken}\` — **${escapeBackticks(sku)}** · ${escapeBackticks(displayText)}`);
			lines.push(`  ${escapeBackticks(detail)}`);
		});

		lines.push('');
		totalAppliances += appliances.length;
	});

	lines.push(
		`**Summary:** ${islands.length} island${islands.length === 1 ? '' : 's'}, ` +
		`${totalAppliances} appliance${totalAppliances === 1 ? '' : 's'}. ` +
		`Last modified ${formatLastModified(blueprint)}.`
	);

	lines.push('', '### ID Reference', '| Token | UUID |', '| --- | --- |');
	for (const [token, uuid] of Object.entries(idMap)) {
		lines.push(`| ${token} | ${uuid} |`);
	}

	return { prompt: lines.join('\n'), idMap };
};
